import React, { useEffect, useState, useMemo } from 'react';
import * as R from 'ramda';
import {
  ArrayField,
  Datagrid,
  Error,
  FunctionField,
  Loading,
  TextField,
  useQuery,
} from 'react-admin';
import { Box, Button, makeStyles } from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';

import {
  AssignmentDueDateField,
  AssignmentGradeDistributionField,
  ChipField,
  DeliveriesList,
  DeliveryTitle,
  GradeUpdatesProvider,
  OfflineList,
  useGradeUpdates,
} from 'resources/common';
import { AssignmentProgressField } from 'resources/common/cohorts/list/fields';

const scoreMap = {
  very_good: 5,
  good: 4,
  normal: 3,
  bad: 2,
  very_bad: 1,
};

const DynamicAverageGradeField = ({ record }) => {
  const { getGrade, gradeUpdates } = useGradeUpdates();

  const averageGrade = useMemo(() => {
    const deliveries = R.propOr([], 'deliveries', record);

    const updatedGrades = deliveries.map((delivery) => {
      const studentId = delivery?.id;
      const deliveryName = delivery?.name;
      return getGrade(studentId, deliveryName, delivery?.grade);
    });

    const average = R.pipe(
      R.reject(R.isNil),
      R.map(R.prop(R.__, scoreMap)),
      R.mean,
      (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
    )(updatedGrades);

    return average;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, getGrade, gradeUpdates]);

  return (
    <ChipField
      record={{ average_grade: averageGrade }}
      source="average_grade"
      chipStyle="assignmentGrade"
    />
  );
};

const LinkToStudio = ({ record }) => {
  const baseUrl = process.env.REACT_APP_STUDIO_BASE_URL;
  const courseId = R.pipe(
    R.pathOr('', ['path', 0, 'id']),
    R.split(':'),
    R.last
  )(record);
  const unitId = R.pathOr('', ['path', 3, 'id'])(record);
  const url = `${baseUrl}/container/block-v1:${courseId}+type@vertical+block@${unitId}`;
  return (
    <Button
      size="small"
      color="primary"
      target="_blank"
      href={url}
      label="See In Studio"
    >
      <OpenInNew style={{ paddingRight: '0.2em' }} />
      See In Studio
    </Button>
  );
};

const useListStyles = makeStyles({
  grades: { width: 250 },
  average_grade: { width: 100 },
  hiddenHeader: { display: 'none' },
  hiddenHeaderRow: { display: 'none' },
});

const AssignmentList = ({ record, course, section = null }) => {
  const classes = useListStyles();
  const [assignments, setAssignments] = useState([]);

  const {
    data: apiAssignments,
    loading: assignmentsLoading,
    error: assignmentsError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'deliveries',
    payload: {
      target: 'cohort',
      id: record.id,
      pagination: {
        perPage: 100,
      },
      filter: R.reject(R.isNil, { course, section }),
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  useEffect(() => {
    if (apiAssignments) {
      const tableData = R.pipe(
        R.map((assignment) => {
          const averageGrade = R.pipe(
            R.propOr([], 'deliveries'),
            R.pluck('grade'),
            R.reject(R.isNil),
            R.map(R.prop(R.__, scoreMap)),
            R.mean,
            (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
          )(assignment);
          return R.assoc('average_grade', averageGrade, assignment);
        })
      )(apiAssignments);
      setAssignments(tableData);
    }
  }, [apiAssignments, course]);

  if (assignmentsLoading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (assignmentsError)
    return (
      <Box p="16px">
        <Error error={assignmentsError} errorInfo={assignmentsError} />
      </Box>
    );

  if (R.isEmpty(assignments)) {
    return null;
  }

  const getModuleKey = (item) =>
    R.pathOr('Unknown module', ['path', 1, 'display_name'], item);
  const getSubmoduleKey = (item) =>
    R.pathOr('Unknown submodule', ['path', 2, 'display_name'], item);
  const getUnitKey = (item) =>
    R.pathOr('Unknown unit', ['path', 3, 'display_name'], item);

  const modules = R.pipe(
    R.groupBy(getModuleKey),
    R.toPairs,
    R.map(([moduleName, moduleAssignments]) => ({
      id: moduleName,
      display_name: moduleName,
      assignments: moduleAssignments,
      submodules: R.pipe(
        R.groupBy(getSubmoduleKey),
        R.toPairs,
        R.map(([submoduleName, submoduleAssignments]) => ({
          id: `${moduleName}::${submoduleName}`,
          display_name: submoduleName,
          assignments: submoduleAssignments,
          units: R.pipe(
            R.groupBy(getUnitKey),
            R.toPairs,
            R.map(([unitName, unitAssignments]) => ({
              id: `${moduleName}::${submoduleName}::${unitName}`,
              display_name: unitName,
              assignments: unitAssignments,
            }))
          )(submoduleAssignments),
        })),
        R.reverse
      )(moduleAssignments),
    })),
    R.reverse
  )(assignments);

  const ModuleAssignmentsTable = ({ record }) => {
    const rows = R.pipe(
      R.propOr([], 'submodules'),
      R.chain(R.propOr([], 'units')),
      R.chain(R.propOr([], 'assignments')),
      R.sortBy((it) => R.pathOr('', ['path', 2, 'display_name'], it))
    )(record);

    if (R.isEmpty(rows)) return null;
    return (
      <OfflineList
        list={rows}
        uniqueKey="id"
        defaultSort={{ field: 'path.2.display_name', order: 'ASC' }}
        sorters={{
          assignment_progress: R.pipe(R.propOr([], 'deliveries'), R.length),
          deliveries: R.pipe(
            R.pathOr([], [course, 'deliveries']),
            R.filter(R.propEq('grade', 'very_good')),
            R.length
          ),
        }}
      >
        <Datagrid
          rowClick="expand"
          expand={
            <DeliveriesList record={record} type="assignment" course={course} />
          }
        >
          <DeliveryTitle />
          <FunctionField
            label="Type"
            source="type"
            render={(record) => {
              return record.type === 'file_type' ? 'File Upload' : 'URL';
            }}
          />
          <AssignmentDueDateField label="Due Date" source="due_date" />
          <AssignmentProgressField
            label="Progress"
            source="assignment_progress"
          />
          <AssignmentGradeDistributionField
            label="Grades"
            source="deliveries"
            sortable={false}
            cellClassName={classes.grades}
          />
          <DynamicAverageGradeField
            label="Avg. Grade"
            cellClassName={classes.average_grade}
          />
          <LinkToStudio label="Studio Link" sortable={false} />
        </Datagrid>
      </OfflineList>
    );
  };

  const ModuleDetail = ({ record }) => (
    <ModuleAssignmentsTable record={record} />
  );

  return (
    <ArrayField source="modules" record={{ modules }}>
      <Datagrid rowClick="expand" expand={<ModuleDetail />}>
        <TextField label="Module" source="display_name" sortable={false} />
      </Datagrid>
    </ArrayField>
  );
};

function ByAssignmentsSubtab(props) {
  const { record, course } = props;

  return (
    <GradeUpdatesProvider>
      <AssignmentList record={record} course={course} />
    </GradeUpdatesProvider>
  );
}

export default ByAssignmentsSubtab;
